import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

const TYPE_COLORS = {
  law_firm: '#F59E0B',
  marketer: '#8B5CF6',
  medical_practice: '#10B981',
  insurance_company: '#3B82F6',
  expert_service: '#223368',
  other: '#6B7280',
};

const TYPE_LABELS = {
  law_firm: 'Law Firm',
  marketer: 'Marketer',
  medical_practice: 'Medical Practice',
  insurance_company: 'Insurance Company',
  expert_service: 'Expert Service',
  other: 'Other',
};

export default function NetworkTab() {
  const { currentOrg } = useOrg();
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPartnerships = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await api.getPartnerships(currentOrg.id);
      setPartnerships(data);
    } catch (err) {
      console.error('Failed to fetch partnerships:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchPartnerships(); }, [fetchPartnerships]);

  const handleAccept = async (id) => {
    setActionLoading(id);
    try {
      await api.acceptPartnership(currentOrg.id, id);
      fetchPartnerships();
    } catch (err) {
      console.error('Failed to accept:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (id) => {
    setActionLoading(id);
    try {
      await api.declinePartnership(currentOrg.id, id);
      fetchPartnerships();
    } catch (err) {
      console.error('Failed to decline:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Remove this partnership? Existing appointment collaborations will remain.')) return;
    setActionLoading(id);
    try {
      await api.revokePartnership(currentOrg.id, id);
      fetchPartnerships();
    } catch (err) {
      console.error('Failed to revoke:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const incoming = partnerships.filter(p => p.status === 'pending' && p.target_org_id === currentOrg?.id);
  const outgoing = partnerships.filter(p => p.status === 'pending' && p.requester_org_id === currentOrg?.id);
  const active = partnerships.filter(p => p.status === 'accepted');

  const OrgCard = ({ org, color }) => {
    const initials = org?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';
    const location = [org?.city, org?.state].filter(Boolean).join(', ');
    return (
      <div className="flex items-center gap-3 min-w-0">
        {org?.logo_url ? (
          <img src={org.logo_url} alt="" className="w-10 h-10 rounded-[10px] object-cover flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0" style={{ background: color }}>
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-primary-c truncate">{org?.name}</div>
          <div className="text-[11px] text-muted-c">{location || TYPE_LABELS[org?.type] || ''}</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-tertiary animate-pulse">
            <div className="w-10 h-10 rounded-[10px] bg-hover-surface" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 bg-hover-surface rounded w-2/3" />
              <div className="h-2.5 bg-hover-surface rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 overflow-y-auto h-full">
      {/* Incoming Requests */}
      {incoming.length > 0 && (
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">
            Incoming Requests ({incoming.length})
          </h3>
          <div className="space-y-2">
            {incoming.map(p => {
              const org = p.requester_org;
              const color = TYPE_COLORS[org?.type] || '#6B7280';
              return (
                <div key={p.id} className="p-4 rounded-xl bg-tertiary border border-theme">
                  <OrgCard org={org} color={color} />
                  {p.message && (
                    <p className="text-[12px] text-secondary-c mt-2.5 ml-[52px] italic">"{p.message}"</p>
                  )}
                  <div className="flex gap-2 mt-3 ml-[52px]">
                    <button
                      onClick={() => handleAccept(p.id)}
                      disabled={actionLoading === p.id}
                      className="px-4 py-[7px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleDecline(p.id)}
                      disabled={actionLoading === p.id}
                      className="px-4 py-[7px] rounded-lg text-[12px] font-semibold text-muted-c bg-elevated border border-theme hover:bg-hover-surface transition-all disabled:opacity-50"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Outgoing Pending */}
      {outgoing.length > 0 && (
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">
            Pending Requests ({outgoing.length})
          </h3>
          <div className="space-y-2">
            {outgoing.map(p => {
              const org = p.target_org;
              const color = TYPE_COLORS[org?.type] || '#6B7280';
              return (
                <div key={p.id} className="flex items-center justify-between p-4 rounded-xl bg-tertiary border border-theme">
                  <OrgCard org={org} color={color} />
                  <span className="text-[11px] font-semibold text-warning px-3 py-1 rounded-full bg-warning-soft flex-shrink-0 ml-3">
                    Pending
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Partners */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3">
          Network Partners ({active.length})
        </h3>
        {active.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[32px] mb-3 opacity-40">🤝</div>
            <p className="text-[13px] text-muted-c">No network partners yet</p>
            <p className="text-[12px] text-muted-c mt-1">Use the Map tab to find and request partnerships</p>
          </div>
        ) : (
          <div className="space-y-2">
            {active.map(p => {
              const org = p.requester_org_id === currentOrg?.id ? p.target_org : p.requester_org;
              const color = TYPE_COLORS[org?.type] || '#6B7280';
              return (
                <div key={p.id} className="flex items-center justify-between p-4 rounded-xl bg-tertiary border border-theme group">
                  <OrgCard org={org} color={color} />
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className="text-[11px] font-semibold text-success px-3 py-1 rounded-full bg-success-soft">
                      Connected
                    </span>
                    <button
                      onClick={() => handleRevoke(p.id)}
                      disabled={actionLoading === p.id}
                      className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg text-muted-c hover:text-danger hover:bg-danger-soft flex items-center justify-center transition-all text-[12px] disabled:opacity-50"
                      title="Remove partnership"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
